import { useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { ROLE_LABELS, ROLE_RANK, apiMessage } from "@/lib/team";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RoleSelect } from "@/components/admin/team/RoleSelect";

const COPY = {
  role: { title: "Change role", cta: "Save role", desc: "The new access applies on their next request. The change is audit-logged." },
  suspend: { title: "Suspend access", cta: "Suspend", danger: true, desc: "They're signed out everywhere right away and can't sign in until reactivated." },
  reactivate: { title: "Reactivate access", cta: "Reactivate", desc: "They can sign in again with their existing password. Old sessions stay ended." },
  revoke: { title: "Revoke access", cta: "Revoke access", danger: true, desc: "Permanent. They're signed out everywhere and can never sign in with this account again." },
  resend: { title: "Resend invitation", cta: "Resend", desc: "A new link replaces the old one and is valid for 72 hours." },
  cancel: { title: "Cancel invitation", cta: "Cancel invitation", danger: true, desc: "The invitation link stops working immediately." },
};

function request(kind, row, body) {
  const m = `/admin/team/members/${row.id}`;
  const i = `/admin/team/invitations/${row.id}`;
  return {
    role: () => api.patch(`${m}/role`, body), suspend: () => api.post(`${m}/suspend`, body),
    reactivate: () => api.post(`${m}/reactivate`, body), revoke: () => api.post(`${m}/revoke`, { ...body, confirm: true }),
    resend: () => api.post(`${i}/resend`), cancel: () => api.post(`${i}/cancel`),
  }[kind]();
}

/** One confirmation dialog for every member/invitation action. `action` = { kind, row } or null. */
export function TeamActionDialog({ action, actorIsOwner, onClose, onDone }) {
  const [role, setRole] = useState("");
  const [reason, setReason] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const kind = action?.kind;
  const row = action?.row;
  const c = COPY[kind] || {};
  const raising = kind === "role" && row && role && ROLE_RANK[role] > ROLE_RANK[row.role];
  const needsAck = raising || kind === "revoke";

  useEffect(() => { setRole(row?.role || ""); setReason(""); setAck(false); setError(""); }, [action]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await request(kind, row, kind === "role" ? { role, reason: reason.trim(), confirm_increase: !!raising } : { reason: reason.trim() });
      toast.success(`${c.title} — done for ${row.email}`);
      onDone();
    } catch (err) {
      setError(apiMessage(err, "That didn't work. Try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!action} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`team-action-dialog-${kind}`}>
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{c.title}: {row?.name || row?.email}</DialogTitle>
            <DialogDescription>{c.desc}</DialogDescription>
          </DialogHeader>
          {kind === "role" && <RoleSelect value={role} onChange={setRole} actorIsOwner={actorIsOwner} testid="team-action-role" />}
          {raising && (
            <div className="flex gap-2 text-sm text-amber-800 bg-amber-100 rounded-xl px-3 py-2" data-testid="team-action-raise-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> This raises access from {ROLE_LABELS[row.role]} to {ROLE_LABELS[role]}.
            </div>
          )}
          {["role", "suspend", "reactivate", "revoke"].includes(kind) && (
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500">Reason (optional, kept in the audit log)
              <textarea rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)}
                className="pf-input w-full px-3 py-2 mt-1 text-sm normal-case tracking-normal font-normal" data-testid="team-action-reason" />
            </label>
          )}
          {needsAck && (
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-1 accent-cyan-600" data-testid="team-action-ack" />
              {kind === "revoke" ? "I understand this can't be undone." : "I confirm this person should get the higher access."}
            </label>
          )}
          {error && <p className="text-sm text-red-700 bg-red-50 rounded-xl px-3 py-2" role="alert" data-testid="team-action-error">{error}</p>}
          <DialogFooter className="gap-2">
            <button type="button" onClick={onClose} disabled={busy} className="pf-btn-glass px-4 py-2 rounded-full text-sm" data-testid="team-action-cancel">Close</button>
            <button type="submit" disabled={busy || (needsAck && !ack) || (kind === "role" && role === row?.role)}
              className={`px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 ${c.danger ? "text-white bg-red-600 hover:bg-red-700 transition-colors" : "pf-btn-primary"}`}
              data-testid="team-action-confirm">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{busy ? "Working…" : c.cta}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
